/**
 * WorkflowChartView — renders workflow/process flow diagrams.
 *
 * Uses dagre for automatic graph layout and D3 for SVG rendering.
 * Supports five node types (start, end, action, decision, handoff),
 * directed edges with labels, optional swimlane grouping by actor,
 * and interactive zoom/pan with a reset button.
 */

import { useRef, useEffect, useCallback, useState } from "react";
import * as d3 from "d3";
import dagre from "dagre";
import type { WorkflowChartConfig } from "../../types/index.js";
import { useResizeObserver } from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface WorkflowChartViewProps {
	config: WorkflowChartConfig;
	isModal?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PADDING = 48;
const ARROW_SIZE = 8;

/** Default fill colours per node type. */
const NODE_COLOURS: Record<string, string> = {
	start: "#5DB329",
	end: "#64748B",
	action: "#1B6BC4",
	decision: "#F7A020",
	handoff: "#7C3AED",
};

/** Node dimensions fed to dagre for layout. */
function nodeDimensions(type: string): { width: number; height: number } {
	switch (type) {
		case "decision":
			return { width: 120, height: 120 };
		case "start":
		case "end":
			return { width: 100, height: 40 };
		default:
			return { width: 160, height: 50 };
	}
}

// ---------------------------------------------------------------------------
// Text wrapping helper
// ---------------------------------------------------------------------------

/**
 * Wraps a label into at most two lines that fit within maxWidth characters.
 * Returns an array of line strings.
 */
function wrapLabel(label: string, maxChars = 18): string[] {
	if (label.length <= maxChars) return [label];
	const words = label.split(" ");

	// Single long word with no spaces — truncate with ellipsis rather than overflow
	if (words.length === 1) {
		return [label.substring(0, maxChars - 1) + "…"];
	}

	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		if (current.length === 0) {
			// If the word itself exceeds maxChars, truncate it
			current = word.length > maxChars ? word.substring(0, maxChars - 1) + "…" : word;
		} else if ((current + " " + word).length <= maxChars) {
			current += " " + word;
		} else {
			lines.push(current);
			current = word.length > maxChars ? word.substring(0, maxChars - 1) + "…" : word;
			if (lines.length === 1) {
				// Start of the second (final) line — append remaining words and truncate
				const remaining = words.slice(words.indexOf(word) + 1);
				let second = current;
				for (const w of remaining) {
					if ((second + " " + w).length <= maxChars) {
						second += " " + w;
					} else {
						second = second.substring(0, maxChars - 1) + "…";
						break;
					}
				}
				lines.push(second);
				return lines;
			}
		}
	}
	if (current) lines.push(current);
	return lines;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WorkflowChartView({ config, isModal: _isModal = false }: WorkflowChartViewProps) {
	useRenderLoopGuard("WorkflowChartView");
	const containerRef = useRef<HTMLDivElement>(null);
	const svgRef = useRef<SVGSVGElement>(null);
	const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
	const initialTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
	const { width: containerWidth } = useResizeObserver(containerRef);

	const [tooltipData, setTooltipData] = useState<{ label: string; actor?: string; x: number; y: number } | null>(null);

	const { nodes, edges, direction } = config;

	const hasSwimlanes = direction === "TB" && nodes.some((n) => n.actor);

	const renderChart = useCallback(() => {
		if (!svgRef.current || containerWidth === 0) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		// -----------------------------------------------------------------------
		// Dagre layout
		// -----------------------------------------------------------------------
		const g = new dagre.graphlib.Graph();
		g.setDefaultEdgeLabel(() => ({}));
		g.setGraph({
			rankdir: direction,
			nodesep: 40,
			ranksep: 60,
			marginx: PADDING,
			marginy: PADDING,
		});

		nodes.forEach((node) => {
			const dim = nodeDimensions(node.type);
			g.setNode(node.id, { width: dim.width, height: dim.height, label: node.label });
		});

		edges.forEach((edge) => {
			g.setEdge(edge.source, edge.target, { label: edge.label ?? "" });
		});

		dagre.layout(g);

		const graphInfo = g.graph();
		const totalW = (graphInfo.width ?? 400) + PADDING * 2;
		const totalH = (graphInfo.height ?? 300) + PADDING * 2;

		// Responsive height — at least 300px, grows with content
		const svgH = Math.max(300, totalH);

		svg
			.attr("width", containerWidth)
			.attr("height", svgH)
			.style("height", `${svgH}px`)
			.attr("role", "img");

		// -----------------------------------------------------------------------
		// Defs — arrowhead marker
		// -----------------------------------------------------------------------
		const defs = svg.append("defs");

		defs.append("marker")
			.attr("id", "wf-arrow")
			.attr("viewBox", `0 -${ARROW_SIZE / 2} ${ARROW_SIZE} ${ARROW_SIZE}`)
			.attr("refX", ARROW_SIZE - 1)
			.attr("refY", 0)
			.attr("markerWidth", ARROW_SIZE)
			.attr("markerHeight", ARROW_SIZE)
			.attr("orient", "auto")
			.append("path")
			.attr("d", `M0,${-ARROW_SIZE / 2}L${ARROW_SIZE},0L0,${ARROW_SIZE / 2}`)
			.attr("fill", "#94a3b8");

		// Scale transform for zoom/pan — all content goes inside this group
		const scale = Math.min(1, containerWidth / totalW);
		const translateX = (containerWidth - totalW * scale) / 2;

		const zoomGroup = svg.append("g")
			.attr("class", "wf-zoom-group")
			.attr("transform", `translate(${translateX},0) scale(${scale})`);

		// -----------------------------------------------------------------------
		// Swimlanes (TB only, when any node has an actor)
		// -----------------------------------------------------------------------
		if (hasSwimlanes) {
			// Group nodes by actor — preserve insertion order
			const actorOrder: string[] = [];
			const actorNodes: Map<string, typeof nodes> = new Map();

			nodes.forEach((node) => {
				const actor = node.actor ?? "Unassigned";
				if (!actorNodes.has(actor)) {
					actorOrder.push(actor);
					actorNodes.set(actor, []);
				}
				actorNodes.get(actor)!.push(node);
			});

			const LABEL_WIDTH = 90;

			actorOrder.forEach((actor, i) => {
				const actorNodeList = actorNodes.get(actor)!;

				// Find the y-range of this actor's nodes in the layout
				let minY = Infinity;
				let maxY = -Infinity;
				actorNodeList.forEach((n) => {
					const pos = g.node(n.id);
					if (!pos) return;
					const dim = nodeDimensions(n.type);
					minY = Math.min(minY, pos.y - dim.height / 2);
					maxY = Math.max(maxY, pos.y + dim.height / 2);
				});

				if (minY === Infinity) return;

				const bandY = minY - 16;
				const bandH = maxY - minY + 32;

				// Alternating background bands
				zoomGroup.append("rect")
					.attr("x", PADDING)
					.attr("y", bandY)
					.attr("width", totalW - PADDING * 2)
					.attr("height", bandH)
					.attr("fill", i % 2 === 0 ? "#f8fafc" : "#f1f5f9")
					.attr("rx", 6)
					.attr("opacity", 0.7);

				// Swimlane label on the left
				zoomGroup.append("text")
					.attr("class", "workflow-view__swimlane-label")
					.attr("x", PADDING + LABEL_WIDTH / 2)
					.attr("y", bandY + bandH / 2)
					.attr("text-anchor", "middle")
					.attr("dominant-baseline", "middle")
					.attr("font-family", "var(--font-body, system-ui)")
					.attr("font-size", "10px")
					.attr("font-weight", "600")
					.attr("fill", "#64748b")
					.attr("letter-spacing", "0.04em")
					.attr("transform", `rotate(-90, ${PADDING + LABEL_WIDTH / 2}, ${bandY + bandH / 2})`)
					.text(actor.toUpperCase());
			});
		}

		// -----------------------------------------------------------------------
		// Edges
		// -----------------------------------------------------------------------
		const lineGenerator = d3.line<{ x: number; y: number }>()
			.x((d) => d.x)
			.y((d) => d.y)
			.curve(d3.curveBasis);

		edges.forEach((edge) => {
			const edgeData = g.edge(edge.source, edge.target);
			if (!edgeData?.points) return;

			// Get target node to shorten path so arrow doesn't overlap
			const targetNode = g.node(edge.target);
			const points = edgeData.points as Array<{ x: number; y: number }>;

			// Trim the last point slightly so the arrow tip sits at the node border
			const trimmedPoints = [...points];
			if (trimmedPoints.length >= 2 && targetNode) {
				const last = trimmedPoints[trimmedPoints.length - 1];
				const prev = trimmedPoints[trimmedPoints.length - 2];
				const dx = last.x - prev.x;
				const dy = last.y - prev.y;
				const len = Math.sqrt(dx * dx + dy * dy);
				if (len > 0) {
					const trim = 6;
					trimmedPoints[trimmedPoints.length - 1] = {
						x: last.x - (dx / len) * trim,
						y: last.y - (dy / len) * trim,
					};
				}
			}

			// Draw the path
			zoomGroup.append("path")
				.attr("d", lineGenerator(trimmedPoints) ?? "")
				.attr("fill", "none")
				.attr("stroke", "#94a3b8")
				.attr("stroke-width", 1.5)
				.attr("marker-end", "url(#wf-arrow)");

			// Edge label at midpoint
			if (edge.label) {
				const mid = trimmedPoints[Math.floor(trimmedPoints.length / 2)];

				// White background rect for readability
				const labelGroup = zoomGroup.append("g")
					.attr("transform", `translate(${mid.x}, ${mid.y})`);

				const text = labelGroup.append("text")
					.attr("text-anchor", "middle")
					.attr("dominant-baseline", "middle")
					.attr("font-family", "var(--font-body, system-ui)")
					.attr("font-size", "10px")
					.attr("fill", "#475569")
					.attr("font-weight", "500")
					.text(edge.label);

				// Background rect sized around text (approximate — use getBBox if available)
				const textEl = text.node();
				const bbox = textEl?.getBBox?.();
				if (bbox) {
					labelGroup.insert("rect", "text")
						.attr("x", bbox.x - 3)
						.attr("y", bbox.y - 2)
						.attr("width", bbox.width + 6)
						.attr("height", bbox.height + 4)
						.attr("fill", "#ffffff")
						.attr("rx", 3)
						.attr("opacity", 0.9);
				}
			}
		});

		// -----------------------------------------------------------------------
		// Nodes
		// -----------------------------------------------------------------------
		nodes.forEach((node) => {
			const pos = g.node(node.id);
			if (!pos) return;

			const dim = nodeDimensions(node.type);
			const colour = node.colour ?? NODE_COLOURS[node.type] ?? "#1B6BC4";
			const cx = pos.x;
			const cy = pos.y;
			const hw = dim.width / 2;
			const hh = dim.height / 2;

			const nodeGroup = zoomGroup.append("g")
				.attr("class", "wf-node")
				.attr("transform", `translate(${cx}, ${cy})`)
				.style("cursor", "default")
				.attr("role", "img")
				.attr("aria-label", `${node.type} node: ${node.label}${node.actor ? `, assigned to ${node.actor}` : ""}`);

			// ---- Draw shape ----
			switch (node.type) {
				case "start":
				case "end": {
					const shape = nodeGroup.append("rect")
						.attr("x", -hw)
						.attr("y", -hh)
						.attr("width", dim.width)
						.attr("height", dim.height)
						.attr("rx", hh) // Full pill
						.attr("fill", colour)
						.attr("stroke", "none");

					if (node.type === "end") {
						// Double border effect — add inner ring
						nodeGroup.append("rect")
							.attr("x", -hw + 3)
							.attr("y", -hh + 3)
							.attr("width", dim.width - 6)
							.attr("height", dim.height - 6)
							.attr("rx", hh - 3)
							.attr("fill", "none")
							.attr("stroke", "#fff")
							.attr("stroke-width", 1.5)
							.attr("opacity", 0.6);
					}

					// Hover effect
					shape
						.on("mouseenter", function (event: MouseEvent) {
							d3.select(this).attr("filter", "brightness(1.15)");
							showTooltip(event, node.label, node.actor);
						})
						.on("mousemove", function (event: MouseEvent) {
							moveTooltip(event);
						})
						.on("mouseleave", function () {
							d3.select(this).attr("filter", null);
							hideTooltip();
						});
					break;
				}

				case "decision": {
					// Diamond — rotated square
					const r = Math.min(hw, hh) - 2;
					const diamond = nodeGroup.append("polygon")
						.attr("points", `0,${-r} ${r},0 0,${r} ${-r},0`)
						.attr("fill", colour)
						.attr("stroke", "none");

					diamond
						.on("mouseenter", function (event: MouseEvent) {
							d3.select(this).attr("opacity", "0.85");
							showTooltip(event, node.label, node.actor);
						})
						.on("mousemove", function (event: MouseEvent) {
							moveTooltip(event);
						})
						.on("mouseleave", function () {
							d3.select(this).attr("opacity", "1");
							hideTooltip();
						});
					break;
				}

				case "handoff": {
					// Dashed-border rectangle
					const shape = nodeGroup.append("rect")
						.attr("x", -hw)
						.attr("y", -hh)
						.attr("width", dim.width)
						.attr("height", dim.height)
						.attr("rx", 8)
						.attr("fill", colour)
						.attr("fill-opacity", 0.12)
						.attr("stroke", colour)
						.attr("stroke-width", 1.5)
						.attr("stroke-dasharray", "5,3");

					// Slightly different hover for handoff since fill is light
					shape
						.on("mouseenter", function (event: MouseEvent) {
							d3.select(this).attr("fill-opacity", "0.2");
							showTooltip(event, node.label, node.actor);
						})
						.on("mousemove", function (event: MouseEvent) {
							moveTooltip(event);
						})
						.on("mouseleave", function () {
							d3.select(this).attr("fill-opacity", "0.12");
							hideTooltip();
						});
					break;
				}

				default: {
					// action — solid rounded rect
					const shape = nodeGroup.append("rect")
						.attr("x", -hw)
						.attr("y", -hh)
						.attr("width", dim.width)
						.attr("height", dim.height)
						.attr("rx", 8)
						.attr("fill", colour)
						.attr("stroke", "none");

					shape
						.on("mouseenter", function (event: MouseEvent) {
							d3.select(this).attr("filter", "brightness(1.12)");
							showTooltip(event, node.label, node.actor);
						})
						.on("mousemove", function (event: MouseEvent) {
							moveTooltip(event);
						})
						.on("mouseleave", function () {
							d3.select(this).attr("filter", null);
							hideTooltip();
						});
					break;
				}
			}

			// ---- Node label ----
			const textColour = node.type === "handoff" ? colour : "#ffffff";
			const maxChars = node.type === "decision" ? 12 : node.type === "start" || node.type === "end" ? 14 : 20;
			const lines = wrapLabel(node.label, maxChars);
			const lineHeight = 14;
			const totalTextH = lines.length * lineHeight;
			const startY = -totalTextH / 2 + lineHeight / 2;

			lines.forEach((line, i) => {
				nodeGroup.append("text")
					.attr("x", 0)
					.attr("y", startY + i * lineHeight)
					.attr("text-anchor", "middle")
					.attr("dominant-baseline", "middle")
					.attr("font-family", "var(--font-body, system-ui)")
					.attr("font-size", "12px")
					.attr("font-weight", node.type === "start" || node.type === "end" ? "600" : "500")
					.attr("fill", textColour)
					.attr("pointer-events", "none")
					.text(line);
			});

			// ---- Actor label under node (for non-swimlane layouts) ----
			if (node.actor && !hasSwimlanes) {
				nodeGroup.append("text")
					.attr("x", 0)
					.attr("y", hh + 13)
					.attr("text-anchor", "middle")
					.attr("font-family", "var(--font-body, system-ui)")
					.attr("font-size", "9px")
					.attr("fill", "#94a3b8")
					.attr("pointer-events", "none")
					.text(node.actor);
			}
		});

		// -----------------------------------------------------------------------
		// Zoom behaviour
		// -----------------------------------------------------------------------
		const zoom = d3.zoom<SVGSVGElement, unknown>()
			.scaleExtent([0.3, 2.0])
			.on("zoom", (event) => {
				zoomGroup.attr("transform", event.transform.toString());
			});

		zoomRef.current = zoom;

		// Set initial transform to the pre-calculated centred/scaled position
		const initialTransform = d3.zoomIdentity
			.translate(translateX, 0)
			.scale(scale);

		// Store so the reset button can return to the same position
		initialTransformRef.current = initialTransform;

		svg.call(zoom).call(zoom.transform, initialTransform);

	}, [containerWidth, nodes, edges, direction, hasSwimlanes]);

	// -----------------------------------------------------------------------
	// Tooltip helpers (DOM-based for performance)
	// -----------------------------------------------------------------------

	function showTooltip(event: MouseEvent, label: string, actor?: string) {
		setTooltipData({ label, actor, x: event.clientX, y: event.clientY });
	}

	function moveTooltip(event: MouseEvent) {
		setTooltipData((prev) => prev ? { ...prev, x: event.clientX, y: event.clientY } : null);
	}

	function hideTooltip() {
		setTooltipData(null);
	}

	function handleResetZoom() {
		if (!svgRef.current || !zoomRef.current) return;
		const svg = d3.select(svgRef.current);
		svg.transition().duration(300).call(
			zoomRef.current.transform,
			initialTransformRef.current
		);
	}

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	// Tooltip positioning — clamp to viewport
	const tooltipStyle: React.CSSProperties = tooltipData
		? {
				left: Math.min(tooltipData.x + 12, window.innerWidth - 180),
				top: Math.max(tooltipData.y - 20, 8),
				opacity: 1,
		  }
		: { opacity: 0 };

	// Guard — insufficient data (kept inside JSX to honour Rules of Hooks)
	if (!nodes || nodes.length < 2) {
		return (
			<div className="workflow-view__empty" role="status">
				Insufficient data to render workflow.
			</div>
		);
	}

	return (
		<div ref={containerRef} className="workflow-view">
			{/* Reset zoom button */}
			<button
				className="workflow-view__reset-btn"
				onClick={handleResetZoom}
				aria-label="Reset zoom to fit"
				title="Reset zoom"
			>
				<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="11" height="11" aria-hidden="true">
					<path d="M1 7a6 6 0 1 0 6-6 6 6 0 0 0-5.5 3.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
					<path d="M1 1v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
				Reset
			</button>

			{/* SVG canvas */}
			<svg
				ref={svgRef}
				style={{ display: "block", width: "100%", height: 0, overflow: "visible" }}
				aria-label={`Workflow diagram with ${nodes.length} steps`}
			/>

			{/* Tooltip */}
			<div
				className="workflow-view__tooltip"
				role="tooltip"
				style={tooltipStyle}
			>
				<strong>{tooltipData?.label}</strong>
				{tooltipData?.actor && (
					<span className="workflow-view__tooltip-actor">{tooltipData.actor}</span>
				)}
			</div>
		</div>
	);
}
